"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { API_ENDPOINTS, getApiErrorDetail, useApi } from "@/lib/api"
import { BackfillCandidate } from "@/types/payroll"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { invalidateHubCounts } from "@/lib/hub-counts-refresh"
import { formatNumber } from "@/lib/locale"
import type { TranslationKey } from "@/translations"
import { Loader2, RefreshCw } from "lucide-react"
import { DEFAULT_PAGE_SIZE, TablePager, pageSlice } from "@/components/table-pager"
import {
  ActiveFilters,
  ChoiceChips,
  FilterSection,
  FiltersButton,
  RowCard,
  RowList,
  RowMeta,
  RowName,
  SearchBox,
  SortChip,
  StatusChip,
  type AccentTone,
  type ActiveFilterChip,
  type SortOption,
} from "@/components/row-card"

interface CandidatesResponse {
  provider: string
  data: BackfillCandidate[]
  hasMore?: boolean
}

const ALL = "__all__"

/** The server list window; also the number quoted in the truncation notice. */
const LIST_TAKE = 500

const STATUS_FILTERS: { value: string; labelKey: TranslationKey }[] = [
  { value: ALL, labelKey: "backfill.filter.anyStatus" },
  { value: "ready", labelKey: "backfill.status.ready" },
  { value: "typeNotSent", labelKey: "backfill.status.typeNotSent" },
]

type SortKey = "workerName" | "profileTypeCode" | "status"

const SORT_OPTION_KEYS: { key: SortKey; labelKey: TranslationKey }[] = [
  { key: "workerName", labelKey: "workers.sort.worker" },
  { key: "profileTypeCode", labelKey: "field.profileTypeValue" },
  { key: "status", labelKey: "common.status" },
]

/** Status sorts in action order: addable first, then type-not-sent. */
function statusRank(c: BackfillCandidate): number {
  return c.typeIsSent ? 0 : 1
}

function sortValue(
  c: BackfillCandidate,
  key: SortKey,
  fallbackName: (c: BackfillCandidate) => string
): string | number {
  switch (key) {
    case "workerName":
      return (c.workerName || fallbackName(c)).toLowerCase()
    case "profileTypeCode":
      return (c.profileTypeValue || c.profileTypeCode).toLowerCase()
    case "status":
      return statusRank(c)
  }
}

/**
 * Add existing workers: workers who were already active before sending was
 * turned on (or who were missed) are listed here; running the backfill puts
 * them through the same pipeline as a fresh approval, so every outcome lands
 * on the Workers tab.
 */
export function BackfillPage() {
  const { payrollApi } = useApi()
  const { toast } = useToast()
  const { t, language } = useTranslation()

  /** Display name for a worker whose name the API did not return. */
  const profileName = useCallback(
    (c: { userProfileId: number }) => t("workers.profileNumber", { id: c.userProfileId }),
    [t]
  )

  const [provider, setProvider] = useState<string>("")
  const [candidates, setCandidates] = useState<BackfillCandidate[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [isLoading, setIsLoading] = useState(true)
  const [isRunning, setIsRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [nameFilter, setNameFilter] = useState("")
  const [typeFilter, setTypeFilter] = useState(ALL)
  const [statusFilter, setStatusFilter] = useState<string>(ALL)
  const [sortKey, setSortKey] = useState<SortKey>("workerName")
  const [sortAsc, setSortAsc] = useState(true)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const changeSortKey = (key: SortKey) => {
    setSortKey(key)
    setSortAsc(true)
    setPage(1)
  }

  const [filtersOpen, setFiltersOpen] = useState(false)

  const clearFilters = () => {
    setTypeFilter(ALL)
    setStatusFilter(ALL)
    setPage(1)
  }

  const load = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      // Server-paged (take + hasMore, like the migrations list) — a large
      // tenant must not arrive as one multi-megabyte response.
      const response = await payrollApi.get<CandidatesResponse>(
        `${API_ENDPOINTS.PAYROLL.BACKFILL_CANDIDATES}?take=${LIST_TAKE}`
      )
      setProvider(response.provider ?? "")
      setCandidates(response.data ?? [])
      setHasMore(response.hasMore === true)
      setSelectedIds(new Set())
      setPage(1)
    } catch (err) {
      // Server error text is shown as-is; only the fallback is translated.
      setError(getApiErrorDetail(err) ?? t("backfill.error.load"))
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const workerTypes = useMemo(() => {
    const byCode = new Map<string, string>()
    for (const c of candidates) {
      if (!byCode.has(c.profileTypeCode)) byCode.set(c.profileTypeCode, c.profileTypeValue || c.profileTypeCode)
    }
    return Array.from(byCode, ([code, value]) => ({ code, value })).sort((a, b) => a.value.localeCompare(b.value))
  }, [candidates])

  const filtered = useMemo(() => {
    const name = nameFilter.trim().toLowerCase()
    const result = candidates.filter((c) => {
      if (name && !(c.workerName || profileName(c)).toLowerCase().includes(name)) return false
      if (typeFilter !== ALL && c.profileTypeCode !== typeFilter) return false
      if (statusFilter === "ready" && !c.typeIsSent) return false
      if (statusFilter === "typeNotSent" && c.typeIsSent) return false
      return true
    })
    return result.sort((a, b) => {
      const av = sortValue(a, sortKey, profileName)
      const bv = sortValue(b, sortKey, profileName)
      const cmp = typeof av === "number" && typeof bv === "number" ? av - bv : String(av).localeCompare(String(bv))
      return sortAsc ? cmp : -cmp
    })
  }, [candidates, nameFilter, typeFilter, statusFilter, sortKey, sortAsc, profileName])

  const visible = pageSlice(filtered, page, pageSize)

  // Individually selectable: everything listed — the server already excludes
  // tracked workers, and a deliberate click on a "type not sent" worker is
  // allowed and fails loud.
  const selectable = filtered

  // "Select all" covers only workers that would actually send — one click
  // must not queue a Failed record for every type-not-sent row.
  const addable = useMemo(() => selectable.filter((c) => c.typeIsSent), [selectable])

  // Changing a filter drops selections that fell out of view — otherwise
  // "Add selected" could quietly queue workers the operator can no longer see.
  useEffect(() => {
    const allowed = new Set(selectable.map((c) => c.userProfileId))
    setSelectedIds((prev) => {
      const kept = [...prev].filter((id) => allowed.has(id))
      return kept.length === prev.size ? prev : new Set(kept)
    })
  }, [selectable])

  const allSelected = addable.length > 0 && addable.every((c) => selectedIds.has(c.userProfileId))

  const toggle = (id: number, checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })

  // Merge/subtract rather than replace, so deliberate per-row picks of
  // "type not sent" workers survive a select-all toggle.
  const toggleAll = (checked: boolean) =>
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const c of addable) {
        if (checked) next.add(c.userProfileId)
        else next.delete(c.userProfileId)
      }
      return next
    })

  const sortOptions: SortOption<SortKey>[] = SORT_OPTION_KEYS.map((o) => ({ key: o.key, label: t(o.labelKey) }))

  const activeChips: ActiveFilterChip[] = [
    ...(typeFilter !== ALL
      ? [
          {
            key: "type",
            // Worker-type names are the tenant's own code-table Values.
            label: workerTypes.find((ty) => ty.code === typeFilter)?.value ?? typeFilter,
            onRemove: () => {
              setTypeFilter(ALL)
              setPage(1)
            },
          },
        ]
      : []),
    ...(statusFilter !== ALL
      ? [
          {
            key: "status",
            label: (() => {
              const match = STATUS_FILTERS.find((s) => s.value === statusFilter)
              return match ? t(match.labelKey) : statusFilter
            })(),
            onRemove: () => {
              setStatusFilter(ALL)
              setPage(1)
            },
          },
        ]
      : []),
  ]

  const run = async () => {
    if (selectedIds.size === 0) return
    setIsRunning(true)
    try {
      const response = await payrollApi.post<{ queued: number }>(API_ENDPOINTS.PAYROLL.BACKFILL_RUN, {
        userProfileIds: Array.from(selectedIds),
      })
      toast({
        title:
          response.queued === 1
            ? t("backfill.toast.queuedOne")
            : t("backfill.toast.queued", { count: formatNumber(response.queued, language) }),
        description: t("backfill.toast.queuedDescription"),
      })
      // Adding workers moves both hub figures: more tracked, fewer to add.
      invalidateHubCounts()
      void load()
    } catch (err) {
      toast({
        variant: "destructive",
        title: t("backfill.toast.addFailed"),
        description: getApiErrorDetail(err) ?? String(err),
      })
    } finally {
      setIsRunning(false)
    }
  }

  /** The primary action's label — count-aware, so no string is built by hand. */
  const addButtonLabel =
    selectedIds.size === 0
      ? t("backfill.addSelected")
      : selectedIds.size === 1
        ? t("backfill.addSelectedOne")
        : t("backfill.addSelectedCount", { count: formatNumber(selectedIds.size, language) })

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {/* Provider name is the provider's own; only the "not configured yet" stand-in is translated. */}
        {t("backfill.intro", { provider: provider || t("backfill.yourPayrollSystem") })}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <SearchBox
          placeholder={t("backfill.searchPlaceholder")}
          aria-label={t("backfill.searchPlaceholder")}
          value={nameFilter}
          onChange={(e) => {
            setNameFilter(e.target.value)
            setPage(1)
          }}
        />
        <FiltersButton activeCount={activeChips.length} expanded={filtersOpen} onClick={() => setFiltersOpen(true)} />
        <div className="ml-auto flex items-center gap-2">
          <SortChip
            options={sortOptions}
            sortKey={sortKey}
            asc={sortAsc}
            onKeyChange={changeSortKey}
            onDirectionToggle={() => setSortAsc((v) => !v)}
          />
          <Button
            variant="outline"
            size="icon"
            className="h-8 w-8"
            aria-label={t("common.refresh")}
            onClick={() => {
              void load()
              // Another operator may have added workers: refresh the cards too,
              // so the candidate figure cannot contradict the refreshed table.
              invalidateHubCounts()
            }}
            disabled={isLoading || isRunning}
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      <ActiveFilters chips={activeChips} onClearAll={clearFilters} />

      {hasMore && !isLoading && !error && (
        <div className="rounded-[8px] bg-[var(--th-canvas)] px-4 py-2.5 text-sm text-muted-foreground">
          {t("backfill.limitNotice", { count: formatNumber(LIST_TAKE, language) })}
        </div>
      )}

      {/* Filter drawer — every filter lives here, as Timesheet Hub does. */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="right">
          <SheetHeader>
            <SheetTitle className="text-xl">{t("common.filters")}</SheetTitle>
          </SheetHeader>
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-4">
            <FilterSection label={t("field.profileTypeValue")}>
              <ChoiceChips
                ariaLabel={t("backfill.filter.byWorkerTypeAria")}
                value={typeFilter}
                onChange={(v) => {
                  setTypeFilter(v)
                  setPage(1)
                }}
                options={[
                  { value: ALL, label: t("backfill.filter.allTypes"), count: candidates.length },
                  ...workerTypes.map((ty) => ({
                    value: ty.code,
                    label: ty.value,
                    count: candidates.filter((c) => c.profileTypeCode === ty.code).length,
                  })),
                ]}
              />
            </FilterSection>
            <FilterSection label={t("common.status")}>
              <ChoiceChips
                ariaLabel={t("backfill.filter.byStatusAria")}
                value={statusFilter}
                onChange={(v) => {
                  setStatusFilter(v)
                  setPage(1)
                }}
                options={STATUS_FILTERS.map((s) => ({
                  value: s.value,
                  label: t(s.labelKey),
                  count:
                    s.value === ALL
                      ? candidates.length
                      : s.value === "ready"
                        ? candidates.filter((c) => c.typeIsSent).length
                        : candidates.filter((c) => !c.typeIsSent).length,
                }))}
              />
            </FilterSection>
          </div>
          <SheetFooter>
            <Button variant="ghost" className="rounded-full" onClick={clearFilters}>
              {t("common.clearFilters")}
            </Button>
            <Button className="ml-auto rounded-full" onClick={() => setFiltersOpen(false)}>
              {t("common.done")}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {error ? (
        <Card>
          <CardContent className="space-y-3 py-8 text-center text-sm">
            <p className="text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => void load()}>
              {t("common.tryAgain")}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-4">
              <label htmlFor="backfill-select-all" className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  id="backfill-select-all"
                  checked={allSelected}
                  onCheckedChange={(v) => toggleAll(v === true)}
                />
                {t("backfill.selectAllReady")}
              </label>
              <p className="text-xs text-muted-foreground" aria-live="polite">
                {isLoading
                  ? t("common.loading")
                  : t("workers.countOf", {
                      shown: formatNumber(visible.length, language),
                      total: formatNumber(filtered.length, language),
                    })}
              </p>
            </div>
            {/* The "Ping all (25)" position: the bulk action rides the count row. */}
            <Button
              className="rounded-full"
              size="sm"
              onClick={() => void run()}
              disabled={isRunning || selectedIds.size === 0}
            >
              {isRunning && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {addButtonLabel}
            </Button>
          </div>
          <RowList>
            {isLoading ? (
              <div className="flex h-24 items-center justify-center rounded-[14px] bg-white">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : visible.length === 0 ? (
              <div className="flex h-24 items-center justify-center rounded-[14px] bg-white text-sm text-muted-foreground">
                {t("workers.empty")}
              </div>
            ) : (
              visible.map((c) => {
                const tone: AccentTone = c.typeIsSent ? "info" : "warning"
                const name = c.workerName || profileName(c)
                return (
                  <RowCard key={c.userProfileId} accent={tone}>
                    <div className="flex flex-wrap items-center gap-3">
                      <Checkbox
                        aria-label={t("backfill.selectWorkerAria", { name })}
                        checked={selectedIds.has(c.userProfileId)}
                        onCheckedChange={(v) => toggle(c.userProfileId, v === true)}
                      />
                      <div className="min-w-[220px] flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <RowName>{name}</RowName>
                          {/* The tenant's own code-table Value for the type. */}
                          <Badge variant="warning" title={c.profileTypeCode}>
                            {c.profileTypeValue || c.profileTypeCode}
                          </Badge>
                        </div>
                        <RowMeta label={t("workers.meta.profile")}>#{c.userProfileId}</RowMeta>
                      </div>
                      <div className="flex items-center">
                        {c.typeIsSent ? (
                          <StatusChip tone="info">{t("backfill.status.ready")}</StatusChip>
                        ) : (
                          <StatusChip tone="warning" title={t("backfill.status.typeNotSentHint")}>
                            {t("backfill.status.typeNotSentLong")}
                          </StatusChip>
                        )}
                      </div>
                    </div>
                  </RowCard>
                )
              })
            )}
          </RowList>
          <TablePager
            totalCount={filtered.length}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        </div>
      )}
    </div>
  )
}
